import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { RxCross2 } from "react-icons/rx";
import { FaFile } from "react-icons/fa";
import { toast } from "react-toastify";
import swal from "sweetalert";

export default function QuickBookEstimateAdd() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const customersRef = useRef([]);
  const productsRef = useRef([]);

  const TAX_RATE_DEFAULT = 6;

  const generateEstimateNo = () => {
    const prefix = "EST";
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const random = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}-${date}-${random}`;
  };

  const generateTrackingNo = () => {
    const prefix = "TRK";
    const timestamp = Date.now();
    return `${prefix}-${timestamp}`;
  };

  const [formData, setFormData] = useState({
    CustomerRef: "",
    BillEmail: "",
    BillAddr: "",
    ShipAddr: "",
    billingAddress: {
      bill_line1: "",
      bill_line2: "",
      bill_city: "",
      bill_country: "",
      bill_postal_code: "",
    },
    shippingAddress: {
      ship_line1: "",
      ship_line2: "",
      ship_city: "",
      ship_country: "",
      ship_postal_code: "",
    },
    ShipVia: "",
    ShipDate: "",
    TrackingNum: generateTrackingNo(),
    DocNumber: generateEstimateNo(),
    TxnDate: new Date().toISOString().split("T")[0],
    ExpirationDate: "",
    AcceptedBy: "",
    AcceptedDate: "",
    po_number: "",
    vendor_details: "",
    shipping_corp: "",
    Line: [
      {
        DetailType: "SalesItemLineDetail",
        SalesItemLineDetail: {
          ItemRef: { value: "", name: "" },
          UnitPrice: 0,
          Qty: 1,
        },
        Description: "",
        Amount: 0,
        item_name: "",
      },
    ],
    tax_percent: TAX_RATE_DEFAULT,
    discount_value: 0,
    discount_type: "fixed",
    deposit_percent: 0,
    CustomerMemo: "",
    PrivateNote: "",
    file: "",
    TotalAmt: 0,
    total_shipping: 0,
  });

  const fetchInitialData = async () => {
    loader(true);
    try {
      const [custRes, prodRes] = await Promise.all([
        ApiClient.post("quickbook/customers", {
          quickbook_token: user?.quick_book_accesToken || "",
          company: user?.quick_book_companyId,
        }),
        ApiClient.post("quickbook/items", {
          quickbook_token: user?.quick_book_accesToken || "",
          company: user?.quick_book_companyId,
        }),
      ]);

      const custList = custRes?.success ? custRes.data || [] : [];
      const prodList = prodRes?.success ? prodRes.data || [] : [];

      customersRef.current = custList;
      productsRef.current = prodList;
      setCustomers(custList);
      setProducts(prodList);
    } catch (err) {
      console.error(err);
    }
    loader(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const subtotal = formData.TotalAmt;
  const discountAmt =
    formData.discount_type === "percent"
      ? (subtotal * Number(formData.discount_value)) / 100
      : Number(formData.discount_value) || 0;

  const taxPercent = Number(formData.tax_percent) || 0;
  const taxAmt = ((subtotal - discountAmt) * taxPercent) / 100;

  const totalShippingAmt = Number(formData.total_shipping) || 0;
  const estimateTotal = subtotal - discountAmt + taxAmt + totalShippingAmt;
  const depositAmt = (estimateTotal * Number(formData.deposit_percent)) / 100;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.Line];

    if (field === "ItemRef") {
      const product = products.find((p) => p.Id === value);
      if (!product) return;
      newLines[index].SalesItemLineDetail.ItemRef = {
        value: product.Id,
        name: product.Name,
      };
      newLines[index].item_name = product.Name;
      newLines[index].Description = product.Description || "";
      newLines[index].SalesItemLineDetail.UnitPrice = product.UnitPrice || 0;
    } else if (field === "Qty") {
      newLines[index].SalesItemLineDetail.Qty = Number(value);
    } else if (field === "UnitPrice") {
      newLines[index].SalesItemLineDetail.UnitPrice = Number(value);
    } else {
      newLines[index][field] = value;
    }

    newLines[index].Amount =
      newLines[index].SalesItemLineDetail.Qty *
      newLines[index].SalesItemLineDetail.UnitPrice;

    const total = newLines.reduce((acc, l) => acc + l.Amount, 0);
    setFormData((prev) => ({ ...prev, Line: newLines, TotalAmt: total }));
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      Line: [
        ...prev.Line,
        {
          DetailType: "SalesItemLineDetail",
          SalesItemLineDetail: {
            ItemRef: { value: "", name: "" },
            UnitPrice: 0,
            Qty: 1,
          },
          Description: "",
          Amount: 0,
          item_name: "",
        },
      ],
    }));
  };

  const removeLine = (index) => {
    const newLines = formData.Line.filter((_, i) => i !== index);
    const total = newLines.reduce((acc, l) => acc + l.Amount, 0);
    setFormData((prev) => ({ ...prev, Line: newLines, TotalAmt: total }));
  };

  const showError = (msg) =>
    swal({
      icon: "error",
      title: "Validation Error",
      text: msg,
      confirmButtonColor: "#115B9D",
      confirmButtonText: "OK",
    });

  const validate = () => {
    if (!formData.CustomerRef) {
      showError("Please select a customer");
      return false;
    }
    if (!formData.BillEmail) {
      showError("Customer email is required");
      return false;
    }
    if (!formData.TxnDate) {
      showError("Estimate date is required");
      return false;
    }
    if (formData.Line.length === 0) {
      showError("Please add at least one line item");
      return false;
    }
    for (let i = 0; i < formData.Line.length; i++) {
      const line = formData.Line[i];
      if (!line.SalesItemLineDetail.ItemRef.value && !line?.item_name) {
        showError(`Please select a product for line ${i + 1}`);
        return false;
      }
      if (
        !line.SalesItemLineDetail.UnitPrice ||
        line.SalesItemLineDetail.UnitPrice <= 0
      ) {
        showError(`Please enter a rate greater than 0 for line ${i + 1}`);
        return false;
      }
      if (!line.SalesItemLineDetail.Qty || line.SalesItemLineDetail.Qty <= 0) {
        showError(`Qty must be greater than 0 for line ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    loader(true);
    try {
      const Line = formData.Line.map((l) => ({
        amount: l.Amount,
        DetailType: "SalesItemLineDetail",
        SalesItemLineDetail: {
          ItemRef: {
            value: l.SalesItemLineDetail.ItemRef.value || l.value || "",
            name: l.SalesItemLineDetail.ItemRef.name || l.item_name || "",
          },
          Qty: l.SalesItemLineDetail.Qty,
          UnitPrice: l.SalesItemLineDetail.UnitPrice,
        },
        Description: l.Description,
      }));

      const product_line_items = formData.Line.map((l) => ({
        amount: l.Amount,
        DetailType: "SalesItemLineDetail",
        SalesItemLineDetail: {
          ItemRef: {
            value: l.SalesItemLineDetail.ItemRef.value || l.value || "",
            name: l.SalesItemLineDetail.ItemRef.name || l.item_name || "",
          },
          Qty: l.SalesItemLineDetail.Qty,
          UnitPrice: l.SalesItemLineDetail.UnitPrice,
        },
        Description: l.Description,
      }));

      const payload = {
        quickbook_token: user?.quick_book_accesToken || "",
        company: user?.quick_book_companyId,
        CustomerRef: { value: formData?.CustomerRef || null },
        BillAddr: {
          Line1: formData.billingAddress?.bill_line1,
          City: formData.billingAddress?.bill_city,
          Country: formData.billingAddress?.bill_country,
          PostalCode: formData.billingAddress?.bill_postal_code,
        },
        ShipAddr: {
          Line1: formData.shippingAddress?.ship_line1,
          City: formData.shippingAddress?.ship_city,
          Country: formData.shippingAddress?.ship_country,
          PostalCode: formData.shippingAddress?.ship_postal_code,
        },
        Line,
        product_line_items,
        ShipMethodRef: {
          value: "1",
          name: "Ship via",
        },
        ShipDate: formData.ShipDate,
        TrackingNum: formData.TrackingNum,
        DocNumber: formData.DocNumber,
        TxnDate: formData.TxnDate,
        ExpirationDate: formData.ExpirationDate,
        CustomerMemo: { value: formData?.CustomerMemo },
        TxnTaxDetail: { TxnTaxCodeRef: { value: "TAX" } },
        PrivateNote: `
Memo: ${formData.PrivateNote || ""}
Accepted By: ${formData.AcceptedBy || ""}
Accepted Date: ${formData.AcceptedDate || ""}
Shipping Corp: ${formData.shipping_corp || ""}
Vendor: ${formData.vendor_details || ""}
PoNumber: ${formData.po_number || ""}
`,
        tax_percent: Number(formData.tax_percent),
        total_tax: taxAmt,
        discount_value: Number(formData.discount_value),
        discount_type: formData.discount_type,
        file: formData.file,
      };

      const res = await ApiClient.post("quickbook/create/quote", payload);
      if (res?.success) {
        loader(false);
        await swal({
          icon: "success",
          title: "Success!",
          text: "Estimate created successfully",
          confirmButtonColor: "#115B9D",
          timer: 2000,
          timerProgressBar: true,
        });
        navigate(-1);
      }
    } catch (err) {
      swal({
        icon: "error",
        title: "Error",
        text: "An error occurred while creating the estimate",
        confirmButtonColor: "#115B9D",
      });
    }
    loader(false);
  };

  const extensionsAllow = ["doc", "pdf"];

  const uploadFile = (e) => {
    let files = e.target.files;
    let file = files?.item(0);

    if (!file) return;
    const fileExtension = file?.name?.split(".").pop().toLowerCase();
    let isAllow = extensionsAllow.includes(fileExtension);
    if (!isAllow) {
      toast.error(
        "Please upload a valid file. Accepted formats include document files in DOC and PDF formats.",
      );
      return;
    }
    loader(true);
    ApiClient.postFormFileData("upload/document", { file }).then((res) => {
      if (res.success) {
        setFormData({ ...formData, file: res?.fileName });
      }
      loader(false);
    });
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto max-w-6xl py-5">
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 px-5 sm:px-6 py-6">
          <div className="flex gap-y-2 gap-x-3 flex-wrap justify-between items-center mb-8 border-b pb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-[#115B9D]">
              Estimate
            </h1>
            <div className="max-[380px]:text-left text-right">
              <p className="text-sm text-gray-500 font-medium">
                Amount (hidden):
              </p>
              <p className="text-xl font-bold text-gray-900">
                ${formData.TotalAmt.toFixed(2)}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Customer Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              <div className="md:col-span-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Customer
                </label>
                <select
                  name="CustomerRef"
                  className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  value={formData.CustomerRef}
                  onChange={(e) => {
                    const cust = customers.find((c) => c.Id === e.target.value);
                    const custBillAddr = cust
                      ? [
                          cust.BillAddr?.Line1,
                          cust.BillAddr?.Line2,
                          cust.BillAddr?.City,
                          cust.BillAddr?.Country,
                          cust.BillAddr?.PostalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "";
                    const custShipAddr = cust
                      ? [
                          cust.ShipAddr?.Line1,
                          cust.ShipAddr?.Line2,
                          cust.ShipAddr?.City,
                          cust.ShipAddr?.Country,
                          cust.ShipAddr?.PostalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "";
                    setFormData((prev) => ({
                      ...prev,
                      CustomerRef: e.target.value,
                      BillEmail: cust?.PrimaryEmailAddr?.Address || "",
                      BillAddr: custBillAddr,
                      ShipAddr: custShipAddr,
                      billingAddress: {
                        bill_line1: cust?.BillAddr?.Line1 || "",
                        bill_line2: cust?.BillAddr?.Line2 || "",
                        bill_city: cust?.BillAddr?.City || "",
                        bill_country: cust?.BillAddr?.Country || "",
                        bill_postal_code: cust?.BillAddr?.PostalCode || "",
                      },
                      shippingAddress: {
                        ship_line1: cust?.ShipAddr?.Line1 || "",
                        ship_line2: cust?.ShipAddr?.Line2 || "",
                        ship_city: cust?.ShipAddr?.City || "",
                        ship_country: cust?.ShipAddr?.Country || "",
                        ship_postal_code: cust?.ShipAddr?.PostalCode || "",
                      },
                    }));
                  }}
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.DisplayName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Customer Email
                </label>
                <input
                  type="email"
                  name="BillEmail"
                  className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  value={formData.BillEmail}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Address / Dates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {/* Bill to */}
              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Bill to
                  </label>
                  <textarea
                    name="BillAddr"
                    rows="4"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.BillAddr}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Ship via
                    </label>
                    <input
                      type="text"
                      name="ShipVia"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                      value="Ship via"
                      disabled
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Shipping date
                    </label>
                    <input
                      type="date"
                      name="ShipDate"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                      value={formData.ShipDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Tracking no.
                  </label>
                  <input
                    type="text"
                    name="TrackingNum"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.TrackingNum}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Ship to */}
              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Ship to
                  </label>
                  <textarea
                    name="ShipAddr"
                    rows="4"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.ShipAddr}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Estimate no.
                    </label>
                    <input
                      type="text"
                      name="DocNumber"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                      value={formData.DocNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Estimate date
                    </label>
                    <input
                      type="date"
                      name="TxnDate"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                      value={formData.TxnDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Expiration date
                  </label>
                  <input
                    type="date"
                    name="ExpirationDate"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.ExpirationDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Ship from / Accepted */}
              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Ship from (hidden)
                  </label>
                  <textarea
                    rows="4"
                    readOnly
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none transition-all border-gray-300"
                    value="2000 Avenue P Ste 9&#10;Riviera Beach, FL 33404"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Accepted by
                    </label>
                    <input
                      type="text"
                      name="AcceptedBy"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                      value={formData.AcceptedBy}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Accepted date
                    </label>
                    <input
                      type="date"
                      name="AcceptedDate"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                      value={formData.AcceptedDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Extra fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 pt-4 border-t border-gray-50">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Tops Shipping Systems, Corp
                </label>
                <input
                  type="text"
                  name="shipping_corp"
                  className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  value={formData.shipping_corp}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  PO Number
                </label>
                <input
                  type="text"
                  name="po_number"
                  className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  value={formData.po_number}
                  onChange={handleInputChange}
                />
                <span className="text-[10px] text-gray-600 mt-1">(hidden)</span>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Vendor Details
                </label>
                <input
                  type="text"
                  name="vendor_details"
                  className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  value={formData.vendor_details}
                  onChange={handleInputChange}
                />
                <span className="text-[10px] text-gray-600 mt-1">(hidden)</span>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Product or service
              </h2>
              <div className="overflow-x-auto border border-gray-200 rounded-2xl">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 w-12">
                        #
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">
                        Product/Service
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-24">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-32">
                        Rate
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-32">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.Line.map((line, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-500">{index + 1}</td>

                        <td className="px-4 py-3">
                          <select
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[150px] bg-white"
                            value={line.SalesItemLineDetail.ItemRef.value}
                            onChange={(e) =>
                              handleLineChange(index, "ItemRef", e.target.value)
                            }
                          >
                            <option value="">Select Product</option>
                            {products.map((p) => (
                              <option key={p.Id} value={p.Id}>
                                {p.Name}
                              </option>
                            ))}
                          </select>
                          {line.item_name &&
                            !line.SalesItemLineDetail.ItemRef.value && (
                              <p className="text-[11px] text-amber-500 mt-1">
                                Requested: {line.item_name}
                              </p>
                            )}
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="text"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[150px]"
                            value={line.Description}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "Description",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[65px] text-right"
                            value={line.SalesItemLineDetail.Qty}
                            onChange={(e) =>
                              handleLineChange(index, "Qty", e.target.value)
                            }
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[100px] text-right"
                            value={line.SalesItemLineDetail.UnitPrice || ""}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "UnitPrice",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td className="px-4 py-3 text-right font-medium">
                          ${line.Amount.toFixed(2)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end flex-wrap gap-2 mt-5">
                <button
                  type="button"
                  onClick={addLine}
                  className="px-4 py-2 text-sm font-medium text-[#115B9D] bg-[#115B9D10] border border-[#115B9D20] rounded-xl hover:bg-[#115B9D20] transition-all"
                >
                  Add product or service
                </button>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-8 lg:gap-12">
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Customer payment options
                  </h3>
                  <textarea
                    name="CustomerMemo"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.CustomerMemo}
                    onChange={handleInputChange}
                    placeholder="Tell your customer how you want to get paid."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Memo on statement (hidden)
                  </label>
                  <textarea
                    name="PrivateNote"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.PrivateNote}
                    onChange={handleInputChange}
                    placeholder="This memo will not show up on your estimate, but will appear on the statement."
                  />
                </div>
                {/* <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Attachments
                  </label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl text-center bg-gray-50 px-5 py-5">
                    {formData?.file ? (
                      <div className="flex items-center justify-center gap-2 relative">
                        <FaFile size={15} />
                        <span className="text-sm">File Uploaded</span>
                        <RxCross2
                          size={17}
                          className="hover:text-red-600 cursor-pointer"
                          onClick={() => setFormData({ ...formData, file: "" })}
                        />
                      </div>
                    ) : (
                      <label
                        htmlFor="dropzone-file"
                        className="text-[#115B9D] font-medium hover:underline cursor-pointer"
                      >
                        Add attachment
                        <input
                          id="dropzone-file"
                          type="file"
                          className="hidden"
                          onChange={(e) => uploadFile(e)}
                        />
                      </label>
                    )}
                  </div>
                </div> */}
              </div>

              {/* Totals */}
              <div className="space-y-4">
                <div className="bg-gray-50/50 rounded-2xl space-y-4 p-5">
                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">Subtotal</span>
                    <span className="text-gray-900 font-semibold">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex gap-2 justify-between items-center text-sm gap-4">
                    <span className="text-gray-500 font-medium shrink-0">
                      Discount
                    </span>
                    <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                      <input
                        type="number"
                        min="0"
                        name="discount_value"
                        className="w-24 p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                        value={formData.discount_value}
                        onChange={handleInputChange}
                      />
                      <select
                        name="discount_type"
                        className="p-1.5 border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.discount_type}
                        onChange={handleInputChange}
                      >
                        <option value="fixed">$</option>
                        <option value="percent">%</option>
                      </select>
                      <span className="text-gray-900 font-semibold w-16 text-right">
                        -${discountAmt.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium shrink-0">
                      Sales tax{" "}
                      <svg
                        className="inline size-3.5 ml-1 text-gray-600"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </span>
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="flex items-center border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-[#115B9D]/30 focus-within:border-[#115B9D] border-gray-200 transition-all">
                        <input
                          type="number"
                          disabled
                          min="0"
                          max="100"
                          step="0.01"
                          name="tax_percent"
                          className="w-16 p-1.5 text-right text-sm text-gray-800 outline-none bg-white"
                          value={formData.tax_percent}
                          onChange={handleInputChange}
                        />
                        <span className="px-2 text-gray-500 text-sm bg-gray-50 border-l border-gray-200 select-none">
                          %
                        </span>
                      </div>
                      <span className="text-gray-900 font-semibold w-20 text-right">
                        ${taxAmt.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-between items-center">
                    <span className="text-base font-bold text-gray-900">
                      Estimate total
                    </span>
                    <span className="text-2xl font-bold text-[#115B9D]">
                      ${estimateTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap items-center justify-end border-t mt-12 pt-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
